import { useState, useEffect, useCallback } from 'react';
import config from '../config/environment';

export interface CredentialField {
  student_name?: string;
  course_name?: string;
  completion_date?: string;
  grade?: string;
  instructor_name?: string;
  [key: string]: any;
}

export interface CredentialTemplate {
  id: string;
  width: number;
  height: number;
  backgroundColor: string;
  fields: Array<{
    field_name: string;
    x_position: number;
    y_position: number;
    width: number;
    height: number;
    font_size: number;
    font_weight: string;
    text_align: string;
    color: string;
  }>;
}

export interface Credential {
  id: string;
  schema_name: string;
  schema_id: string;
  issuer_name: string;
  organization_name: string;
  status: string;
  issued_at: string;
  fields: CredentialField;
  template_file_id?: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface CredentialsResponse {
  credentials: Credential[];
  pagination: PaginationInfo;
}

interface UseCredentialsReturn {
  credentials: Credential[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  refreshCredentials: () => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

export const useCredentials = (): UseCredentialsReturn => {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPageState] = useState<number>(1);
  const [limit, setLimitState] = useState<number>(20);

  const fetchCredentials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token from localStorage
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        setLoading(false);
        setError('No authentication token found. Please log in.');
        return;
      }

      // Use the JWT token directly
      const accessToken = token;

      const response = await fetch(`${config.REACT_APP_API_BASE_URL}/api/credentials/my-credentials?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access forbidden. You may not have permission to view credentials.');
        } else {
          throw new Error(`Failed to fetch credentials: ${response.status} ${response.statusText}`);
        }
      }

      const data: CredentialsResponse = await response.json();
      
      setCredentials(data.credentials);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch credentials';
      setError(errorMessage);
      console.error('Error fetching credentials:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const refreshCredentials = useCallback(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(newLimit);
  }, []);

  return {
    credentials,
    pagination,
    loading,
    error,
    refreshCredentials,
    setPage,
    setLimit,
  };
}; 